/**
 * The one place for how money, dates and times read on screen: the English UI's
 * style, always in Europe/Tallinn, whatever the browser's own locale or zone.
 *
 * Money: "€5,921.20", "−€5.00". Dates: "19 Sep 2026", "19 Sep". Times: "14:30".
 * Missing or invalid input reads "—".
 *
 * Only for display. Machine formats stay where they are: <input type="date"> and
 * datetime-local values, API parameters and keys (usually the `en-CA` calls).
 */

const TIME_ZONE = 'Europe/Tallinn'
const EMPTY = '—'
const MINUS = '−'

// Month and weekday names are spelled out here, not taken from Intl: newer ICU
// data abbreviates September as "Sept" in British and Irish English.
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A Date, an ISO string, a "YYYY-MM-DD" calendar day or epoch milliseconds. */
export type DateInput = Date | string | number | null | undefined

export interface MoneyFormatOptions {
  /** ISO 4217 code; EUR when left out. */
  currency?: string | null
}

export interface DayFormatOptions {
  /** Start with the short weekday: "Sat 19 Sep". */
  weekday?: boolean
}

// ── Money ────────────────────────────────────────────────────────────────

const moneyFormats = new Map<string, Intl.NumberFormat>()

function moneyFormat(currency: string): Intl.NumberFormat {
  let format = moneyFormats.get(currency)
  if (!format) {
    format = new Intl.NumberFormat('en-IE', { style: 'currency', currency })
    moneyFormats.set(currency, format)
  }
  return format
}

/** "€5,921.20"; a negative amount gets a true minus sign: "−€5.00". */
export function formatMoney(
  value: number | string | null | undefined,
  { currency }: MoneyFormatOptions = {},
): string {
  if (value == null || (typeof value === 'string' && !value.trim())) return EMPTY
  const amount = Number(value)
  if (!Number.isFinite(amount)) return EMPTY
  const code = (currency || 'EUR').toUpperCase()
  let text: string
  let zero: string
  try {
    text = moneyFormat(code).format(Math.abs(amount))
    zero = moneyFormat(code).format(0)
  } catch {
    // Not a currency Intl knows: still show the amount, with the code.
    text = `${Math.abs(amount).toFixed(2)} ${code}`
    zero = `${(0).toFixed(2)} ${code}`
  }
  // No sign on an amount that rounds to nothing, so never "−€0.00".
  return amount < 0 && text !== zero ? `${MINUS}${text}` : text
}

// ── Dates and times ──────────────────────────────────────────────────────

/** A moment as the clock on a wall in Tallinn shows it. */
interface WallClock {
  year: number
  /** 1–12 */
  month: number
  day: number
  hour: number
  minute: number
  /** The moment itself, in epoch milliseconds. */
  time: number
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

let tallinnFormat: Intl.DateTimeFormat | null = null

function inTallinn(date: Date): WallClock {
  if (!tallinnFormat) {
    tallinnFormat = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
  }
  const part: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {}
  for (const { type, value } of tallinnFormat.formatToParts(date)) part[type] = Number(value)
  return {
    year: part.year ?? 0,
    month: part.month ?? 1,
    day: part.day ?? 1,
    hour: part.hour ?? 0,
    minute: part.minute ?? 0,
    time: date.getTime(),
  }
}

function wallClock(value: DateInput): WallClock | null {
  if (value == null) return null
  let input: Date | string | number = value
  if (typeof input === 'string') {
    input = input.trim()
    if (!input) return null
    // A date without a time is that calendar day, not UTC midnight.
    const dateOnly = DATE_ONLY.exec(input)
    if (dateOnly) {
      const [year, month, day] = dateOnly.slice(1).map(Number)
      const time = Date.UTC(year, month - 1, day)
      const check = new Date(time)
      // Date.UTC rolls 2026-02-30 over into March; that is not a date.
      if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null
      return { year, month, day, hour: 0, minute: 0, time }
    }
  }
  const date = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(date.getTime())) return null
  return inTallinn(date)
}

const pad = (n: number) => String(n).padStart(2, '0')
const clock = (c: WallClock) => `${pad(c.hour)}:${pad(c.minute)}`
const dayMonth = (c: WallClock) => `${c.day} ${SHORT_MONTHS[c.month - 1]}`
const weekday = (c: WallClock) => SHORT_WEEKDAYS[new Date(Date.UTC(c.year, c.month - 1, c.day)).getUTCDay()]
const sameDay = (a: WallClock, b: WallClock) => a.year === b.year && a.month === b.month && a.day === b.day

/** "19 Sep 2026"; with `weekday`, "Sat 19 Sep 2026". */
export function formatDate(value: DateInput, options: DayFormatOptions = {}): string {
  const c = wallClock(value)
  if (!c) return EMPTY
  const date = `${dayMonth(c)} ${c.year}`
  return options.weekday ? `${weekday(c)} ${date}` : date
}

/** "19 Sep", for when the year is clear from context; with `weekday`, "Sat 19 Sep". */
export function formatDayMonth(value: DateInput, options: DayFormatOptions = {}): string {
  const c = wallClock(value)
  if (!c) return EMPTY
  return options.weekday ? `${weekday(c)} ${dayMonth(c)}` : dayMonth(c)
}

/** "September 2026". */
export function formatMonthYear(value: DateInput): string {
  const c = wallClock(value)
  return c ? `${LONG_MONTHS[c.month - 1]} ${c.year}` : EMPTY
}

/** "14:30", on the 24-hour clock. */
export function formatTime(value: DateInput): string {
  const c = wallClock(value)
  return c ? clock(c) : EMPTY
}

/** "19 Sep 2026, 14:30". */
export function formatDateTime(value: DateInput): string {
  const c = wallClock(value)
  return c ? `${dayMonth(c)} ${c.year}, ${clock(c)}` : EMPTY
}

/**
 * "14:00–14:30" today; "19 Sep, 14:00–14:30" on another day ("19 Sep 2025, …" in
 * another year). A range over two days dates both ends that aren't today:
 * "19 Sep, 23:00 – 20 Sep, 01:00". With no end, or the same moment twice, it is
 * just the start. No start reads "—".
 */
export function formatTimeRange(start: DateInput, end: DateInput): string {
  const from = wallClock(start)
  if (!from) return EMPTY
  const to = wallClock(end)
  const today = inTallinn(new Date())
  const at = (c: WallClock) => {
    if (sameDay(c, today)) return clock(c)
    const day = c.year === today.year ? dayMonth(c) : `${dayMonth(c)} ${c.year}`
    return `${day}, ${clock(c)}`
  }
  if (!to || to.time === from.time) return at(from)
  if (sameDay(from, to)) return `${at(from)}–${clock(to)}`
  return `${at(from)} – ${at(to)}`
}
